import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import { useCan } from "@/app/_utils/sessionClient";
import { PERMISSIONS } from "@/app/_utils/permissions";
import type { AdminUser } from "@/app/_services/RbacService";
import {
  STATUS_BADGE_CLASSES,
  STATUS_DOT_CLASSES,
  STATUS_LABELS,
} from "./userStatus";
import {
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
  UserCheck,
  UserX,
  Users,
} from "lucide-react";

export default function UserList({
  users,
  currentUserId,
  onEdit,
  onResend,
  onSuspend,
  onReactivate,
  onDelete,
}: {
  users: AdminUser[];
  currentUserId?: number;
  onEdit: (user: AdminUser) => void;
  onResend: (user: AdminUser) => void;
  onSuspend: (user: AdminUser) => void;
  onReactivate: (user: AdminUser) => void;
  onDelete: (user: AdminUser) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const canWrite = useCan(PERMISSIONS.RBAC_USERS_WRITE);

  const displayName = (user: AdminUser) =>
    `${user.first_name} ${user.last_name}`.trim() || user.email;

  const actionsMenu = (user: AdminUser) => {
    // Suspending or deleting yourself is rejected server-side; hiding it here keeps the
    // menu honest rather than offering an action that always errors.
    const isSelf = user.id === currentUserId;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            <MoreHorizontal className="size-4 shrink-0" size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(user)}>
            <Pencil className="mr-2 size-4" />
            Edit roles
          </DropdownMenuItem>
          {user.status === "pending" && (
            <DropdownMenuItem onClick={() => onResend(user)}>
              <Send className="mr-2 size-4" />
              Resend invitation
            </DropdownMenuItem>
          )}
          {user.status === "suspended" ? (
            <DropdownMenuItem onClick={() => onReactivate(user)}>
              <UserCheck className="mr-2 size-4" />
              Restore access
            </DropdownMenuItem>
          ) : (
            !isSelf && (
              <DropdownMenuItem onClick={() => onSuspend(user)}>
                <UserX className="mr-2 size-4" />
                Suspend
              </DropdownMenuItem>
            )
          )}
          {!isSelf && (
            <DropdownMenuItem
              className="text-red-600 focus:bg-red-50 focus:text-red-700"
              onClick={() => setPendingDelete(user)}
            >
              <Trash2 className="mr-2 size-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[820px] table-fixed", users.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[30%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              User
            </TableHead>
            <TableHead className="h-8 w-[25%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Roles
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[18%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Last sign in
            </TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={5} className="h-full p-0">
                <CuoEmpty icon={Users} text="No users" />
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id} className="group hover:bg-muted/50">
                <TableCell className="py-1.5">
                  <div className="flex flex-col">
                    <span className="truncate font-medium">{displayName(user)}</span>
                    <span className="truncate text-[12px] text-muted-foreground">
                      {user.email}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-1.5">
                  <span className="truncate text-[12.5px]">
                    {user.roles.length
                      ? user.roles.map((role) => role.name).join(", ")
                      : "No role"}
                  </span>
                </TableCell>
                <TableCell className="py-1.5">
                  <Badge
                    variant="outline"
                    className={`gap-1.5 rounded-[7px] font-normal ${STATUS_BADGE_CLASSES[user.status]}`}
                  >
                    <span
                      className={`size-1.5 flex-none rounded-full ${STATUS_DOT_CLASSES[user.status]}`}
                    />
                    {STATUS_LABELS[user.status]}
                  </Badge>
                </TableCell>
                <TableCell className="py-1.5 text-[12.5px] text-muted-foreground">
                  {user.last_login_at ? formatUtcDate(user.last_login_at) : "Never"}
                </TableCell>
                <TableCell className="w-12 py-1.5 pr-6 text-right">
                  {canWrite && actionsMenu(user)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <CuoConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete user"
        message={`${pendingDelete?.email} will lose access immediately and their invitation will be revoked.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
